import { useRef, useEffect, useState, Dispatch, SetStateAction, FC } from 'react'
import { Button, Center, Stack, Tooltip, Box } from '@chakra-ui/react'

import { CustomFeedback } from 'components'
import { PhotographicCamera } from 'components/icons'
import handleFormatFileToUploadFile from 'components/uploads/Upload/utils/formatFileToUploadFile'
import { useUploadManager } from 'components/uploads/Upload/contexts/UploadManager/Provider'

type Props = {
  setPhoto: Dispatch<SetStateAction<string | null>>
}

const CameraWebcamWithPhoto: FC<Props> = ({ setPhoto }) => {
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user')
  const [error, setError] = useState<string>('')

  const { setFile } = useUploadManager()

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const closeAllCameras = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
    }
  }

  const takePhoto = () => {
    if (canvasRef.current && videoRef.current) {
      const width = videoRef.current.videoWidth
      const height = videoRef.current.videoHeight
      const context = canvasRef.current.getContext('2d')
      canvasRef.current.width = width
      canvasRef.current.height = height
      context?.drawImage(videoRef.current, 0, 0, width, height)
      const imageData = canvasRef.current.toDataURL('image/png')
      fetch(imageData)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], 'foto.jpg', { type: 'image/jpeg' })
          const url = URL.createObjectURL(file)
          setPhoto(url)
          const uploadFile = handleFormatFileToUploadFile(file)
          setFile(uploadFile)
        })
    }
  }

  const startCamera = async (mode: 'user' | 'environment') => {
    closeAllCameras()

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode },
      })

      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
    } catch (err: any) {
      if (err.message === 'Requested device not found') {
        setError('Nenhuma câmera encontrada no dispositivo')
      } else {
        setError('Acesso negado a câmera')
      }
    }
  }

  const toggleCamera = () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user'
    setFacingMode(newMode)
  }

  useEffect(() => {
    startCamera(facingMode)
    return () => {
      closeAllCameras()
    }
  }, [facingMode])

  if (error !== '') {
    return <CustomFeedback context="withoutCamera" title={error} />
  }

  return (
    <Stack align="center">
      <Center borderWidth="2px" rounded="md" borderColor="primary" bg="bgContent">
        <Box as="video" minWidth="100%" ref={videoRef} style={{ borderRadius: '0.25rem' }} playsInline muted />
      </Center>

      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <Tooltip label="Voce não possui outra câmera">
        <Button
          display={{ base: 'none', sm: 'none', md: 'none', lg: 'block', xl: 'block', '2xl': 'block' }}
          isDisabled
          size="md"
          minWidth="100%"
          variant="outline"
          onClick={toggleCamera}
        >
          Câmera {facingMode === 'user' ? 'traseira' : 'frontal'}
        </Button>
      </Tooltip>

      <Button
        display={{ base: 'block', sm: 'block', md: 'block', lg: 'none', xl: 'none', '2xl': 'none' }}
        size="md"
        minWidth="100%"
        variant="outline"
        onClick={toggleCamera}
      >
        Câmera {facingMode === 'user' ? 'traseira' : 'frontal'}
      </Button>

      <Button size="md" minWidth="100%" leftIcon={<PhotographicCamera />} onClick={takePhoto}>
        Tirar foto
      </Button>
    </Stack>
  )
}

export default CameraWebcamWithPhoto
